import React, {useCallback} from 'react';

import {type SettingType} from '../../../../services/analytics/workspaceSettingsEvents';
import {type SettingOverrideCounts} from '../../../../state/views/workspaceSettings/settingOverrideUtils';
import {useLinePlotMaxRunsDynamicConfig} from '../../../../util/rampFeatureFlags/linePlotDynamicConfigs';
import {useRampFlagFullFidelityRunsPerChart} from '../../../../util/rampFeatureFlags/linePlotFeatureFlags';
import {MaxRunsWarningBanner} from '../../../MaxRuns/MaxRunsWarningBanner';
import {useMaxRunsWarningBanner} from '../../../MaxRuns/useMaxRunsWarningBanner';
import {ClearSettingsRows} from '../components/ClearSettingsRow';
import {ResetSettingButton} from '../components/ResetSettingButton';
import {SettingLabel} from '../components/SettingLabel';
import {SettingSlider} from '../components/SettingSlider';
import {useClearSettingRows} from '../hooks/useClearSettingRows';
import {type ClearSettingFnType} from '../hooks/useSettingOverrides';
import {
  type DerivedPointVisualizationOption,
  isPointVizFullFidelity,
  type LinePlotSettings,
  type SettingLevel,
} from '../types';
import {areMaxRunsSettingsActive} from '../utils';
import {getMaxRunsCeiling} from './utils/getCascadingMaxRuns';

export type LinePlotMaxRunsProps = {
  clearSetting: ClearSettingFnType;
  displayValues: {
    maxRuns: number;
    pointVisualizationMethod: DerivedPointVisualizationOption;
  };
  numOverrides?: SettingOverrideCounts;
  settingLevel: SettingLevel;
  values: {
    maxRuns: number | undefined;
  };
  trackSetting: (setting: SettingType, value: string | undefined) => void;
  updateSetting: (settings: Partial<LinePlotSettings>) => void;
};

export const LinePlotMaxRuns = ({
  clearSetting,
  displayValues,
  numOverrides,
  settingLevel,
  trackSetting,
  updateSetting,
  values,
}: LinePlotMaxRunsProps) => {
  const clearSettingRows = useClearSettingRows({
    numOverrides,
    settingKey: 'maxRuns',
    settingLevel,
    clearSetting,
    // Note: we're clearing both the workspace and panel setting fields here
    // Ideally, workspace and panel settings use the same field.
    emptySettings: {maxRuns: undefined, limit: undefined},
  });

  const canOverrideMaxRuns = useRampFlagFullFidelityRunsPerChart();
  const dynamicConfigMaxRuns = useLinePlotMaxRunsDynamicConfig();
  const isFullFidelity = isPointVizFullFidelity(
    displayValues.pointVisualizationMethod
  );
  const runLimit = getMaxRunsCeiling({
    canOverrideMaxRuns,
    isBucketing: isFullFidelity,
    dynamicConfigMaxRuns,
  });

  const showPerfWarning = useMaxRunsWarningBanner({
    isFullFidelity,
    numRuns: displayValues.maxRuns,
  });

  const updateSettingValue = useCallback(
    (newVal: number) => {
      updateSetting({
        maxRuns: newVal,
      });
      trackSetting('maxRuns', newVal.toString());
    },
    [updateSetting, trackSetting]
  );

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-items-center gap-8">
        <SettingLabel
          className="min-w-fit"
          text="Max number of runs or groups"
        />
        <SettingSlider
          name="line-plot-max-runs"
          min={1}
          max={runLimit}
          step={1}
          sliderValue={displayValues.maxRuns}
          updateSetting={updateSettingValue}
        />
        <ResetSettingButton
          isEnabled={areMaxRunsSettingsActive(values)}
          onButtonClick={() =>
            clearSetting(settingLevel, 'limit', {
              limit: undefined,
              maxRuns: undefined,
            })
          }
          settingLevel={settingLevel}
        />
      </div>
      {showPerfWarning && (
        <MaxRunsWarningBanner
          location="workspace-settings"
          numRuns={displayValues.maxRuns}
        />
      )}
      <ClearSettingsRows rows={clearSettingRows} />
    </div>
  );
};
